import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Presentation, X } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { getEventLiveStatus } from "../../utils/eventTimeUtils";
import apiService from "../../services/apiService";

const LiveEventBar = () => {
  const { isAuthenticated } = useAuth();
  const { language, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [dismissed, setDismissed] = useState([]);
  const [now, setNow] = useState(new Date());
  const [joining, setJoining] = useState(false);

  // Fetch upcoming events once on mount and when auth changes
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await apiService.getUpcomingEvents(language, 10);
        if (response.success) {
          setEvents(response.data || []);
        }
      } catch (err) {
        // Silent fail — bar simply won't show
      }
    };
    fetchEvents();
  }, [language, isAuthenticated]);

  // Tick every 30 seconds to re-evaluate time status (no API calls)
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(interval);
  }, []);

  // Find the most relevant live/starting-soon event the user has access to
  const activeEvent = events
    .filter((e) => e.hasAccess)
    .map((e) => ({ ...e, ...getEventLiveStatus(e, now) }))
    .find((e) => e.isLive || e.isStartingSoon);

  const isVisible = activeEvent && !dismissed.includes(activeEvent?.slug);

  // Add bottom padding to body so page content isn't hidden behind the fixed bar
  useEffect(() => {
    if (isVisible) {
      document.body.style.paddingBottom = "80px";
    } else {
      document.body.style.paddingBottom = "";
    }
    return () => { document.body.style.paddingBottom = ""; };
  }, [isVisible]);

  // Don't show if no active event, or dismissed
  if (!activeEvent) return null;
  if (dismissed.includes(activeEvent.slug)) return null;

  const handleDismiss = () => {
    setDismissed((prev) => [...prev, activeEvent.slug]);
  };

  const handleJoin = async () => {
    // Not logged in — redirect to login
    if (!isAuthenticated) {
      navigate(`/login?returnUrl=/event/${activeEvent.slug}`);
      return;
    }

    // Already registered with meeting URL — open directly
    if (activeEvent.isRegistered && activeEvent.meeting_url) {
      window.open(activeEvent.meeting_url, "_blank");
      return;
    }

    // Not registered but eligible — one-click register + join
    if (!activeEvent.isRegistered) {
      setJoining(true);
      // Open window immediately in click handler to avoid popup blockers
      const newWindow = window.open("", "_blank");
      try {
        const response = await apiService.registerForEvent(
          activeEvent.slug,
          "online"
        );
        if (response.success && response.data?.meeting_url) {
          newWindow.location = response.data.meeting_url;
          // Update local state so bar shows as registered
          setEvents((prev) =>
            prev.map((e) =>
              e.slug === activeEvent.slug
                ? {
                    ...e,
                    isRegistered: true,
                    meeting_url: response.data.meeting_url,
                  }
                : e
            )
          );
        } else {
          // Registration succeeded but no meeting URL — go to event page
          newWindow.close();
          navigate(`/event/${activeEvent.slug}`);
        }
      } catch (err) {
        newWindow.close();
        // On error, navigate to event page as fallback
        navigate(`/event/${activeEvent.slug}`);
      } finally {
        setJoining(false);
      }
      return;
    }

    // Registered but no meeting URL — go to event page
    navigate(`/event/${activeEvent.slug}`);
  };

  const isLive = activeEvent.isLive;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-[9999] shadow-2xl ${
        isLive
          ? "bg-[#002855] border-t-2 border-red-500"
          : "bg-orange-500"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
        {/* Left: Image + Status + Event title */}
        <div className="flex items-center gap-3 text-white min-w-0">
          {activeEvent.imageUrl && (
            <img
              src={activeEvent.imageUrl}
              alt={activeEvent.title}
              className="w-20 sm:w-24 aspect-video rounded-lg object-cover flex-shrink-0"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          )}
          {isLive && (
            <span className="relative flex h-3 w-3 flex-shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
            </span>
          )}
          <div className="min-w-0">
            <span className="font-semibold text-sm sm:text-base truncate block">
              {isLive
                ? t({ de: "LIVE JETZT", en: "LIVE NOW" })
                : t({
                    de: `Startet in ${activeEvent.minutesUntilStart} Min.`,
                    en: `Starts in ${activeEvent.minutesUntilStart} min`,
                  })}
              <span className="font-normal opacity-90">
                {" — "}
                {activeEvent.title}
              </span>
            </span>
          </div>
        </div>

        {/* Right: Action buttons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleJoin}
            disabled={joining}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold text-sm sm:text-base transition-colors whitespace-nowrap ${
              isLive
                ? "bg-[#EE005F] text-white hover:bg-[#cc0050]"
                : "bg-white text-orange-600 hover:bg-orange-50"
            } ${joining ? "opacity-70 cursor-wait" : ""}`}
          >
            <Presentation className="w-5 h-5" />
            {joining
              ? t({ de: "Wird geöffnet...", en: "Opening..." })
              : isAuthenticated && !activeEvent.isRegistered
              ? t({ de: "Anmelden & Beitreten", en: "Register & Join" })
              : t({ de: "Jetzt beitreten", en: "Join Now" })}
          </button>
          <button
            onClick={handleDismiss}
            className="text-white/70 hover:text-white p-1"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LiveEventBar;
